import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import { addYears } from 'date-fns';

import DatePickerWrapper from '../../components/Datepicker/DatepickerWrapper';
import { singleSelectComponents } from '../../components/CustomDashboard/multiSelectComponents';

import styles from './ArtifactsPage.module.scss';
import '../../components/CustomDashboard/wf-select-portal.scss';

const pad = (value: number) => String(value).padStart(2, '0');

// the wall-clock form the API layer converts to an aware instant
function toWallClock(date: Date, time: string): string {
  const [hours = '00', minutes = '00'] = (time || '00:00').split(':');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${hours}:${minutes}`;
}

interface TimeOption {
  value: string;
  label: string;
}

const TIME_STEP_MINUTES = 15;
const TIME_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

const timeOption = (value: string): TimeOption => ({ value, label: value });

const TIME_OPTIONS: TimeOption[] = Array.from(
  { length: (24 * 60) / TIME_STEP_MINUTES },
  (_, index) => {
    const minutes = index * TIME_STEP_MINUTES;
    return timeOption(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  },
);

// "9:05" and "09:05" both mean the same minute; anything else is not a time
function normalizeTime(input: string): string | null {
  const match = TIME_PATTERN.exec(input.trim());
  return match ? `${pad(Number(match[1]))}:${match[2]}` : null;
}

const selectPortalProps = {
  menuPortalTarget: document.body,
  menuPosition: 'fixed' as const,
  styles: {
    menuPortal: (base: object) => ({ ...base, zIndex: 10000 }),
    // the "Use 09:37" create row is wider than the two-digit times the control is sized for
    menu: (base: object) => ({
      ...base,
      width: 'auto',
      minWidth: '100%',
      whiteSpace: 'nowrap' as const,
    }),
  },
};

interface ScheduleDateTimeInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onFocus?: () => void;
}

// Scheduling needs a date *and* a time. The shared Datepicker owns the date half so
// the calendar is the same one used everywhere else in the app; the hour rides
// alongside it as a select, since that picker has no time mode and the browser's
// native time popup cannot be styled to match.
export function ScheduleDateTimeInput({
  id,
  value,
  onChange,
  disabled = false,
  onFocus,
}: ScheduleDateTimeInputProps) {
  const { t } = useTranslation();
  const [datePart = '', timePart = ''] = value.split('T');
  const selectedDate = datePart ? new Date(`${datePart}T00:00`) : null;

  // a bundle scheduled off the 15-minute grid still has to show its real time
  const options = useMemo(() => {
    if (!timePart || TIME_OPTIONS.some((option) => option.value === timePart))
      return TIME_OPTIONS;
    return [...TIME_OPTIONS, timeOption(timePart)].sort((a, b) =>
      a.value.localeCompare(b.value),
    );
  }, [timePart]);
  const selected = options.find((option) => option.value === timePart) ?? null;

  return (
    <div className={styles.scheduleRow} onFocus={onFocus}>
      <DatePickerWrapper
        isRange={false}
        initStartDate={selectedDate}
        initEndDate={null}
        disabled={disabled}
        clearable
        showPresets={false}
        monthsShown={1}
        maxDate={addYears(new Date(), 1)}
        emptyLabel={t('Pick a date')}
        onChange={(start: Date | null) =>
          onChange(start ? toWallClock(start, timePart || '09:00') : '')
        }
      />
      <div className={styles.scheduleTime}>
        <CreatableSelect<TimeOption>
          inputId={id}
          className="wf-select-container"
          classNamePrefix="wf-select"
          aria-label={t('Time') as string}
          placeholder={t('Time')}
          options={options}
          value={selected}
          isDisabled={disabled}
          components={singleSelectComponents}
          menuPlacement="auto"
          isValidNewOption={(input, _value, existing) => {
            const time = normalizeTime(input);
            return (
              time !== null &&
              !existing.some((option) => (option as TimeOption).value === time)
            );
          }}
          getNewOptionData={(input) =>
            timeOption(normalizeTime(input) ?? input)
          }
          formatCreateLabel={(input) =>
            t('Use {{time}}', { time: normalizeTime(input) ?? input })
          }
          noOptionsMessage={() => t('Enter a time as HH:MM')}
          onChange={(option) =>
            option &&
            onChange(toWallClock(selectedDate ?? new Date(), option.value))
          }
          {...selectPortalProps}
        />
      </div>
    </div>
  );
}
